import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { mensagemErro } from '../api/axios';
import {
  confirmarLancamento,
  confirmarVarios,
  filtrosDeTitulos,
  visaoTitulos,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  Indicador,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  SelecaoBuscavel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { usePaginacao } from '../hooks/usePaginacao';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { brl, numero } from '../utils/formato';

const CAMPOS = [
  'operadora',
  'competencia',
  'estado',
  'busca',
  // Chegam da tela de envio: "ver os títulos deste lançamento".
  'id_lote',
  'id_arquivo',
];

// Repetível na URL (`ids_arquivo=a&ids_arquivo=b`), porque vem da confirmação
// em lote: N arquivos escolhidos à mão, que não são um envio.
const CAMPO_MULTIPLO = 'ids_arquivo';

/* O tom diz o que fazer, não o que é: âmbar pede ação, verde é descanso,
   cinza é falta de informação — nunca "tudo certo". */
const TOM = {
  a_lancar: 'pendente',
  a_atualizar: 'pendente',
  conferido: 'confirmado',
  sem_titulo: 'arquivado',
};

const CONTA = { recebido: 'Recebido', glosado: 'Glosado', retido: 'Retido' };

export default function Titulos() {
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const [dados, setDados] = useState(null);
  const [opcoes, setOpcoes] = useState(null);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [agindo, setAgindo] = useState(false);
  const [marcados, setMarcados] = useState(() => new Set());
  const [texto, setTexto] = useState(params.get('busca') || '');

  const consulta = useMemo(() => {
    const q = {};
    CAMPOS.forEach((c) => {
      const v = params.get(c);
      if (v) q[c] = v;
    });
    const varios = params.getAll(CAMPO_MULTIPLO);
    if (varios.length) q[CAMPO_MULTIPLO] = varios;
    return q;
  }, [params]);

  const carregar = useCallback(async () => {
    try {
      const [r, f] = await Promise.all([
        visaoTitulos(consulta),
        filtrosDeTitulos(consulta),
      ]);
      setDados(r.data);
      setOpcoes(f.data);
      setErro('');
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível carregar os títulos.'));
    }
  }, [consulta]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O texto digitado e o aplicado são coisas distintas: filtrar a cada tecla
  // faria a lista piscar resultados intermediários.
  useEffect(() => {
    const t = setTimeout(() => {
      if (texto === (params.get('busca') || '')) return;
      const novo = new URLSearchParams(params);
      if (texto) novo.set('busca', texto);
      else novo.delete('busca');
      setParams(novo, { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [texto, params, setParams]);

  const trocar = useCallback(
    (campo, valor) => {
      const novo = new URLSearchParams(params);
      if (valor) novo.set(campo, valor);
      else novo.delete(campo);
      setParams(novo, { replace: true });
      setMarcados(new Set());
    },
    [params, setParams]
  );

  const linhas = dados?.linhas || [];
  // Ordenar ANTES de paginar. A lista inteira está no cliente (a consulta de
  // títulos não tem limite), então a ordenação vale sobre o conjunto todo —
  // ordenar depois da fatia ordenaria só a página aberta, e uma tabela que
  // diz estar ordenada por valor sem estar é pior que nenhuma ordenação.
  const ord = useOrdenacao(linhas);
  const pag = usePaginacao(ord.ordenados, 25);
  const r = dados?.resumo;

  /* Só o que pede ação pode ser marcado: oferecer a caixa num título já
     conferido convidaria a confirmar de novo sem nada ter mudado. */
  const selecionaveis = useMemo(
    () =>
      pag.paginados.filter((x) =>
        ['a_lancar', 'a_atualizar'].includes(x.estado_lancamento)
      ),
    [pag.paginados]
  );

  const alternar = (protocolo) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(protocolo)) novo.delete(protocolo);
      else novo.add(protocolo);
      return novo;
    });

  const confirmarSelecionados = async () => {
    setAgindo(true);
    setErro('');
    try {
      const { data } = await confirmarVarios([...marcados]);
      setAviso(
        `${numero(data.confirmados.length)} título(s) marcados como lançados.` +
          (data.falharam.length
            ? ` ${data.falharam.length} não puderam: ${data.falharam
                .map((f) => f.protocolo)
                .join(', ')}.`
            : '')
      );
      setMarcados(new Set());
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível confirmar.'));
    } finally {
      setAgindo(false);
    }
  };

  const confirmarUm = async (protocolo) => {
    setAgindo(true);
    try {
      await confirmarLancamento(protocolo);
      setAviso(`Título ${protocolo} marcado como lançado.`);
      await carregar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível confirmar.'));
    } finally {
      setAgindo(false);
    }
  };

  const algumFiltro =
    CAMPOS.some((c) => params.get(c)) || params.getAll(CAMPO_MULTIPLO).length > 0;

  if (!dados) return <LoadingSpinner texto="Carregando títulos…" />;

  return (
    <>
      <PageHeader
        titulo="Títulos a lançar"
        subtitulo="O valor que deve estar em cada título do Nasajon — recebido, glosado e retido."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {(params.get('id_lote') ||
        params.get('id_arquivo') ||
        params.getAll(CAMPO_MULTIPLO).length > 0) && (
        <div className="ff-aviso ff-aviso--info">
          <i className="bi bi-funnel" aria-hidden="true" />
          <div>
            Mostrando apenas os títulos afetados por{' '}
            <strong>
              {params.getAll(CAMPO_MULTIPLO).length > 1
                ? `${params.getAll(CAMPO_MULTIPLO).length} arquivos`
                : 'um envio'}
            </strong>
            . Outros títulos pendentes ficam de fora deste recorte.
          </div>
          <button
            type="button"
            className="ff-aviso__fechar"
            onClick={() => setParams({}, { replace: true })}
            aria-label="Ver todos os títulos"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* O aviso que separa esta tela da Baixa por protocolo. Não é zelo:
          somar no Nasajon em vez de substituir reproduz a duplicidade que o
          grão por arquivo existe para impedir. */}
      <div className="ff-aviso ff-aviso--info">
        <i className="bi bi-pencil-square" aria-hidden="true" />
        <div>
          Estes são os valores que devem <strong>estar</strong> no título, não os que
          devem ser somados a ele. Quando o número aparece como{' '}
          <span className="ff-mono">50.000 → 65.000</span>, o lançamento no Nasajon é
          uma <strong>edição</strong> da baixa existente.
        </div>
      </div>

      {r && (
        <div className="ff-grid-tiles mb-3">
          <Indicador
            rotulo="Aguardando lançamento"
            valor={numero(r.qtd_pendentes)}
            natureza={r.qtd_pendentes > 0 ? 'atencao' : 'entrada'}
            nota={`${brl(r.valor_pendente)} em baixas a lançar ou atualizar`}
          />
          <Indicador
            rotulo="Recebido"
            valor={brl(r.total_recebido)}
            natureza="entrada"
            nota="soma das três contas no recorte"
          />
          <Indicador
            rotulo="Glosado"
            valor={brl(r.total_glosado)}
            natureza={Number(r.total_glosado) > 0 ? 'atencao' : 'neutro'}
            nota={`e ${brl(r.total_retido)} de retenção judicial`}
          />
          <Indicador
            rotulo="Em aberto"
            valor={brl(r.total_em_aberto)}
            nota={`de ${brl(r.total_titulo)} faturados`}
          />
        </div>
      )}

      <div className="mb-3">
        <Painel
          titulo="Filtros"
          acoes={
            (algumFiltro || texto) && (
              <Botao
                variante="secundario"
                pequeno
                onClick={() => {
                  setTexto('');
                  setParams({}, { replace: true });
                }}
              >
                Limpar filtros
              </Botao>
            )
          }
        >
          <div className="p-3 d-flex flex-wrap gap-3 align-items-end">
            <SelecaoBuscavel
              rotulo="Situação"
              largura={170}
              textoTodas="Todas"
              valor={params.get('estado') || ''}
              opcoes={(opcoes?.estados || []).map((e) => ({
                valor: e.valor,
                texto: e.texto,
              }))}
              aoMudar={(v) => trocar('estado', v)}
            />
            <SelecaoBuscavel
              rotulo="Operadora"
              largura={170}
              textoTodas="Todas"
              valor={params.get('operadora') || ''}
              opcoes={(opcoes?.operadoras || []).map((o) => ({ valor: o, texto: o }))}
              aoMudar={(v) => trocar('operadora', v)}
            />
            <SelecaoBuscavel
              rotulo="Competência"
              largura={150}
              textoTodas="Todas"
              valor={params.get('competencia') || ''}
              opcoes={(opcoes?.competencias || []).map((c) => ({ valor: c, texto: c }))}
              aoMudar={(v) => trocar('competencia', v)}
            />
            <div style={{ minWidth: 220, flex: '1 1 220px' }}>
              <label style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Buscar</label>
              <Busca
                valor={texto}
                onChange={setTexto}
                placeholder="Protocolo ou operadora"
                id="busca-titulos"
              />
            </div>
          </div>
        </Painel>
      </div>

      <Painel
        titulo="Título a título"
        subtitulo={`${numero(linhas.length)} título(s) no recorte`}
        acoes={
          marcados.size > 0 && (
            <Botao icone="bi-check2-all" onClick={confirmarSelecionados} disabled={agindo}>
              {agindo
                ? 'Confirmando…'
                : `Marcar ${numero(marcados.size)} como lançado(s)`}
            </Botao>
          )
        }
        rodape={linhas.length > 0 && <Paginacao {...pag} rotulo="títulos" />}
      >
        {linhas.length === 0 ? (
          <Vazio
            icone="bi-receipt"
            titulo="Nenhum título neste recorte"
            descricao={
              algumFiltro || texto
                ? 'Tente afrouxar os filtros.'
                : 'Lance um recebimento para que os títulos apareçam aqui.'
            }
          />
        ) : (
          <div className="ff-tabela-wrap">
            <Tabela>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      aria-label="Marcar todos os pendentes desta página"
                      checked={
                        selecionaveis.length > 0 &&
                        selecionaveis.every((x) => marcados.has(x.protocolo))
                      }
                      onChange={(e) =>
                        setMarcados(
                          e.target.checked
                            ? new Set(selecionaveis.map((x) => x.protocolo))
                            : new Set()
                        )
                      }
                    />
                  </th>
                  <Th campo="protocolo" estado={ord}>Protocolo</Th>
                  <Th campo="operadora" estado={ord}>Operadora</Th>
                  <Th campo="total_cobrado" estado={ord} alinhar="right">
                    Título
                    <Ajuda titulo="Título" origem="Bioslab · total cobrado do protocolo">
                      O valor do título a receber no Nasajon. Nasce do protocolo no
                      Bioslab, por fora do FluxFin.
                    </Ajuda>
                  </Th>
                  <Th campo="recebido" estado={ord} alinhar="right">Recebido</Th>
                  <Th campo="qtd_recebimentos" estado={ord} alinhar="right">
                    Entradas
                    <Ajuda
                      titulo="Entradas"
                      origem="competências em que este protocolo recebeu"
                    >
                      Em quantas competências diferentes entrou dinheiro neste
                      protocolo. Receber em parcelas é a regra, não a exceção — e
                      este número diz de quantas vezes o valor ao lado é a soma.
                      Abra o Detalhe para ver quais foram.
                    </Ajuda>
                  </Th>
                  <Th campo="glosado" estado={ord} alinhar="right">Glosado</Th>
                  <Th campo="glosado_recuperado" estado={ord} alinhar="right">
                    Recuperado
                    <Ajuda
                      titulo="Glosa recuperada"
                      origem="itens recusados que voltaram a ser pagos"
                    >
                      O que a operadora havia recusado e pagou depois do recurso.
                      <br />
                      <br />
                      <strong>Já está dentro de Recebido</strong> — o dinheiro
                      voltou num demonstrativo posterior e foi contado lá. Aparece
                      à parte só para ficar visível; somar as duas colunas dobraria
                      o que voltou.
                    </Ajuda>
                  </Th>
                  <Th campo="retido" estado={ord} alinhar="right">
                    Retido
                    <Ajuda titulo="Retenção judicial" origem="lançamento manual">
                      A única das três contas que não vem de arquivo. Entra só por
                      lançamento manual, com justificativa — abra o detalhe do título.
                    </Ajuda>
                  </Th>
                  <Th campo="saldo" estado={ord} alinhar="right">
                    Em aberto
                    <Ajuda titulo="Em aberto" origem="título − recebido − glosado − retido">
                      O que ainda falta receber. Não é uma baixa: é o saldo que sobra
                      no título depois das três.
                    </Ajuda>
                  </Th>
                  <Th campo="rotulo_estado" estado={ord}>Situação</Th>
                  <Th alinhar="right">{''}</Th>
                </tr>
              </thead>
              <tbody>
                {pag.paginados.map((x) => {
                  const pendente = ['a_lancar', 'a_atualizar'].includes(
                    x.estado_lancamento
                  );
                  return (
                    <tr key={x.protocolo}>
                      <td>
                        {pendente && (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            aria-label={`Marcar ${x.protocolo}`}
                            checked={marcados.has(x.protocolo)}
                            onChange={() => alternar(x.protocolo)}
                          />
                        )}
                      </td>
                      <td className="col-codigo">
                        {x.protocolo}
                        {x.titulo_mudou && (
                          <div
                            style={{ fontSize: 12, color: '#9a5b06' }}
                            title="O total cobrado no Bioslab mudou depois da confirmação. Não há baixa a relançar, mas o em aberto mudou."
                          >
                            <i className="bi bi-info-circle me-1" aria-hidden="true" />
                            título mudou no Bioslab
                          </div>
                        )}
                      </td>
                      <td className="col-meta">{x.operadora || '—'}</td>
                      <td className="col-valor">
                        {x.total_cobrado === null ? (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        ) : (
                          brl(x.total_cobrado)
                        )}
                      </td>
                      <Conta linha={x} conta="recebido" />
                      <td className="col-valor">
                        {x.qtd_recebimentos > 0 ? (
                          <span
                            title={
                              x.qtd_recebimentos === 1
                                ? 'Recebeu em 1 competência'
                                : `Recebeu em ${x.qtd_recebimentos} competências`
                            }
                          >
                            {numero(x.qtd_recebimentos)}×
                          </span>
                        ) : (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        )}
                      </td>
                      <Conta linha={x} conta="glosado" />
                      <td className="col-valor">
                        {Number(x.glosado_recuperado) > 0 ? (
                          brl(x.glosado_recuperado)
                        ) : (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        )}
                        {/* Proposta ainda não confirmada. Sem isto a célula
                            mostra "—" num título que tem dinheiro esperando
                            um clique — correto e inútil. Fica separada do
                            valor de propósito: candidato NÃO é recuperação. */}
                        {x.recuperacao_pendente_qtd > 0 && (
                          <div style={{ fontSize: 12, fontWeight: 400 }}>
                            <Link
                              to={`/recuperacoes?protocolo=${encodeURIComponent(
                                x.protocolo
                              )}`}
                              style={{ color: '#9a5b06' }}
                              title={`${x.recuperacao_pendente_qtd} item(ns) detectado(s) como recuperação, aguardando sua confirmação`}
                            >
                              {brl(x.recuperacao_pendente_valor)} a confirmar
                            </Link>
                          </div>
                        )}
                      </td>
                      <Conta linha={x} conta="retido" />
                      <td className="col-valor">
                        {x.saldo === null ? (
                          <span style={{ color: 'var(--ff-border-field)' }}>—</span>
                        ) : (
                          brl(x.saldo)
                        )}
                      </td>
                      <td>
                        <Badge tom={TOM[x.estado_lancamento] || 'arquivado'}>
                          {x.rotulo_estado}
                        </Badge>
                      </td>
                      <td className="col-valor">
                        <div className="d-flex gap-2 justify-content-end">
                          <Botao
                            variante="secundario"
                            pequeno
                            icone="bi-clock-history"
                            onClick={() =>
                              navegar(`/titulos/${encodeURIComponent(x.protocolo)}`)
                            }
                          >
                            Detalhe
                          </Botao>
                          {pendente && (
                            <Botao
                              pequeno
                              icone="bi-check2"
                              onClick={() => confirmarUm(x.protocolo)}
                              disabled={agindo}
                            >
                              Lancei
                            </Botao>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </div>
        )}
      </Painel>

    </>
  );
}

/* Uma conta do título. Quando mudou desde a confirmação, mostra `de → para` —
   nunca o delta sozinho, que convidaria a somar no Nasajon. */
function Conta({ linha, conta }) {
  const valor = linha[conta];
  const mudou = (linha.mudancas || []).find((m) => m.conta === conta);
  return (
    <td className="col-valor">
      {mudou && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--ff-muted)',
            textDecoration: 'line-through',
          }}
          title={`${CONTA[conta]} confirmado anteriormente`}
        >
          {brl(mudou.de)}
        </div>
      )}
      <span
        style={mudou ? { color: '#9a5b06', fontWeight: 600 } : undefined}
        title={mudou ? `Mudou de ${brl(mudou.de)} para ${brl(mudou.para)}` : undefined}
      >
        {brl(valor)}
      </span>
    </td>
  );
}
